import { Component, DestroyRef, EventEmitter, inject, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../../../../services/notifications.service';
import { StackedSidebarService } from '../../../../../services/stacked-sidebar.service';
import { BoardService } from '../../../../../services/board.service';
import { SidebarType } from '../../../../layout/stacked-sidebar/stacked-sidebar.interface';
import { ChatService } from '../../../../../services/chat.service';
import {
  ACTIONS,
  IChatAction,
} from '../../../../layout/chat-sidebar/components/chat-list-item/chat-list-item.component';
import { IChatItem } from '../../../../../interfaces';

@Component({
  selector: 'app-board-v3-card-chats-panel',
  templateUrl: './card-chats-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardChatsPanelComponent {
  @Input() cardId!: string;

  private readonly notificationService = inject(NotificationService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly translateService = inject(TranslateService);
  private readonly boardService = inject(BoardService);
  private readonly chatService = inject(ChatService);
  private readonly destroyRef = inject(DestroyRef);

  protected linking = false;
  protected loading = false;
  protected chatItems: IChatItem[] = [];
  private chatIds: string[] = [];
  private forceReload = false;

  @Input()
  set chats(value: IChatItem[]) {
    const ids = (value ?? []).map((chat) => chat.id);
    const idsChanged = ids.join(',') !== this.chatIds.join(',');
    this.chatIds = ids;
    if (!idsChanged && !this.forceReload) return;

    this.forceReload = false;
    this.loadChats();
  }

  reload(): void {
    this.forceReload = true;
  }

  private loadChats(): void {
    if (!this.chatIds.length) {
      this.chatItems = [];
      return;
    }

    this.loading = true;
    this.chatService
      .getChats({ page: 1, take: 100 }, { ids: this.chatIds })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.chatItems = response?.items ?? [];
          this.loading = false;
        },
        error: () => {
          this.chatItems = [];
          this.loading = false;
        },
      });
  }

  onChatAction(event: IChatAction): void {
    if (event.action === ACTIONS.EXCLUDE_FROM_CARD) {
      this.boardService
        .updateCard(this.cardId, { remove_chat: event.chatId })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();

      return;
    }

    this.loadChats();
  }

  onOpenChat(chat: IChatItem): void {
    this.stackedSidebarService.openChats(chat.id).subscribe();
  }

  onStartChat(): void {
    this.notificationService.showInfo('', this.translateService.instant('coming_soon'));
  }

  onLinkExistingChat(): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.BoardV3LinkChat, width: '420px' }, { excludes: this.chatIds })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const chatId = (result as { chatId?: string } | undefined)?.chatId;
        if (chatId) this.linkChat(chatId);
      });
  }

  private linkChat(chatId: string): void {
    if (!this.cardId || this.linking) return;

    this.linking = true;
    this.boardService
      .updateCard(this.cardId, { add_chat: chatId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.linking = false;
          this.notificationService.showSuccess('', this.translateService.instant('card_saved'));
        },
        error: () => {
          this.linking = false;
          this.notificationService.showError('', this.translateService.instant('failed_to_save_card'));
        },
      });
  }
}
